import React from 'react'
import { useDispatch } from 'react-redux'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '../../common/Tooltip'

import { AdCreativeWithUrls, AdCreative, Role, User } from 'common/api/v1/types'
import { pluralize, useConfirmationDialog, useUser } from '../../../utils'
import { AppDispatch } from '../../../store'

import { UrlParamFilteredSearchBar } from '../../common/Filters/FilterView/FilteredSearchBar'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'
import { deleteAdCreatives } from '../../../redux/actions/adCreativeActions'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  AdCreativeFilterKey,
  makeAdCreativeFilters,
  mapAdCreativeFilterToUrlParam,
  mapUrlParamToAdCreativeFilter,
} from './adCreativeFilter'

interface CommonActionsProps {
  selected: Array<AdCreative['id']>
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  adCreatives: AdCreativeWithUrls[]
  user: User
}

const BatchDeleteButton = ({
  selected,
  setSelected,
  disabledMessage,
}: {
  selected: AdCreativeWithUrls[]
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  disabledMessage?: string
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const showConfirmation = useConfirmationDialog()

  const onClick = () => {
    showConfirmation(
      () => {
        dispatch(deleteAdCreatives(selected.map(({ id }) => id)))
        setSelected([])
      },
      'Are you sure you want to remove the selected ad creatives?',
      selected.length === 1 ? selected[0].name : 'remove ad creatives',
      {
        ok: { text: 'remove', color: 'error' },
      },
    )
  }
  return (
    <Tooltip title={disabledMessage ?? 'Remove selected ad creatives'} disablePortal={false}>
      <span>
        <IconButton aria-label="batch delete" onClick={onClick} disabled={!!disabledMessage}>
          <DeleteIcon />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const CommonActions = ({ adCreatives, selected, setSelected, user }: CommonActionsProps) => {
  const { group } = useUser()
  const adCreativesMap = adCreatives.reduce<{ [key: string]: AdCreative }>(
    (acc, item) => ({ ...acc, [item.id]: item }),
    {},
  )
  const showButtons = user.role === Role.super || selected.every((id) => adCreativesMap[id]?.groupId === group)
  const isOwnerOfAllSelected = selected.every((id) => adCreativesMap[id]?.createdBy === user.id)

  const filters = makeAdCreativeFilters()
  return (
    <Toolbar disableGutters style={{ overflowX: 'clip' }}>
      <Box
        sx={{
          transition: (theme: Theme) =>
            theme.transitions.create('flex-shrink', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
        }}
        style={{
          flexShrink: selected.length > 0 ? 1 : 0,
          flexGrow: 1,
          flexBasis: '100%',
        }}
      >
        <UrlParamFilteredSearchBar
          urlParamCacheKey={'adCreatives'}
          mapFilterToUrlParamFn={mapAdCreativeFilterToUrlParam}
          mapUrlParamToFilterFn={mapUrlParamToAdCreativeFilter}
          searchTokenFilters={filters}
          rawTextFilter={filters.find((f) => f.key === AdCreativeFilterKey.name)!}
        />
      </Box>

      <Box
        sx={{
          transition: (theme: Theme) =>
            // Fade out, i.e. keep visible until contents has left the screen
            theme.transitions.create('visibility', {
              delay: selected.length === 0 ? theme.transitions.duration.shortest : 0,
            }),
        }}
        style={{
          marginLeft: 16,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          visibility: selected.length > 0 ? 'visible' : 'hidden',
        }}
      >
        <div style={{ width: 180, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="subtitle1">{pluralize(selected.length, 'ad creative')} selected</Typography>
        </div>

        {showButtons && (
          <BatchDeleteButton
            selected={adCreatives.filter((d) => selected.includes(d.id))}
            setSelected={setSelected}
            disabledMessage={
              user.role === Role.super || isOwnerOfAllSelected
                ? undefined
                : 'You are not the owner of a selected ad creative'
            }
          />
        )}
      </Box>
    </Toolbar>
  )
}

export default CommonActions
